import React, { useState, useEffect, useMemo } from 'react';
import { MarketingLead, UserProfile, Reminder, SequenceStep } from '../types';
import { storage } from '../services/storage';
import { 
  Calendar, AlertCircle, Clock, CheckCircle2, Mail, Phone, MessageCircle, 
  MessageSquare, Zap, TrendingUp, Users, FileText, MapPin
} from 'lucide-react';

interface MarketingDashboardProps {
  userProfile: UserProfile | null;
  onNavigateToLead: (leadId: string, tab?: 'reminders' | 'sequence') => void;
}

export const MarketingDashboard: React.FC<MarketingDashboardProps> = ({ 
  userProfile, 
  onNavigateToLead 
}) => {
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeads();
  }, [userProfile?.marketingProviderId]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (userProfile?.marketingProviderId) {
        filters.providerId = userProfile.marketingProviderId;
      }
      filters.limit = 1000; // Charger tous les leads pour avoir toutes les tâches
      
      const leadsData = await storage.getMarketingLeads(filters);
      setLeads(leadsData);
    } catch (error: any) {
      console.error('Error loading marketing leads:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calcul des tâches
  const tasks = useMemo(() => {
    const allItems = leads.flatMap(lead => {
      const leadName = lead.firstName && lead.lastName 
        ? `${lead.firstName} ${lead.lastName}` 
        : 'Lead sans nom';
      
      // 1. Reminders non faits
      const reminders = (lead.reminders || [])
        .filter(r => !r.isDone)
        .map(r => ({
          id: r.id,
          date: r.date,
          note: r.note,
          type: 'REMINDER' as const,
          subType: null,
          leadId: lead.id!,
          leadName,
          leadAddress: lead.propertyAddress || '',
          leadCity: lead.propertyCity || '',
          dateObj: new Date(r.date)
        }));

      // 2. Sequence Steps PENDING
      const steps = (lead.sequenceSteps || [])
        .filter(s => s.status === 'PENDING')
        .map(s => ({
          id: s.id,
          date: s.date,
          note: s.title,
          type: 'SEQUENCE' as const,
          subType: s.type, // EMAIL, CALL, WHATSAPP, SMS
          leadId: lead.id!,
          leadName,
          leadAddress: lead.propertyAddress || '',
          leadCity: lead.propertyCity || '',
          dateObj: new Date(s.date)
        }));

      return [...reminders, ...steps];
    });

    // Trier par date
    return allItems.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  }, [leads]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Grouper les tâches
  const overdueTasks = useMemo(() => {
    return tasks.filter(task => {
      const taskDate = new Date(task.date);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate < today;
    });
  }, [tasks, today]);

  const todayTasks = useMemo(() => {
    return tasks.filter(task => {
      const taskDate = new Date(task.date);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate.getTime() === today.getTime();
    });
  }, [tasks, today]);

  const upcomingTasks = useMemo(() => {
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    return tasks.filter(task => {
      const taskDate = new Date(task.date);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate > today && taskDate <= nextWeek;
    });
  }, [tasks, today]);

  const getTaskIcon = (type: string, subType: string | null) => {
    if (type === 'REMINDER') {
      return <Clock className="w-4 h-4" />;
    }
    
    switch(subType) {
      case 'EMAIL': return <Mail className="w-4 h-4" />;
      case 'CALL': return <Phone className="w-4 h-4" />;
      case 'WHATSAPP': return <MessageCircle className="w-4 h-4" />;
      case 'SMS': return <MessageSquare className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const renderTaskCard = (task: typeof tasks[0]) => {
    const taskDate = new Date(task.date);
    const isLate = taskDate < today;
    const isToday = taskDate.getTime() === today.getTime();
    
    return (
      <div
        key={`${task.type}-${task.id}`}
        onClick={() => onNavigateToLead(task.leadId, 'reminders')}
        className={`bg-white p-4 rounded-lg shadow-sm border cursor-pointer hover:shadow-md transition-all ${
          isLate ? 'border-red-200 bg-red-50' :
          isToday ? 'border-amber-200 bg-amber-50' :
          'border-slate-200'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`mt-1 p-2 rounded-lg ${
              isLate ? 'bg-red-100' :
              isToday ? 'bg-amber-100' :
              task.type === 'SEQUENCE' ? 'bg-purple-100' :
              'bg-blue-100'
            }`}>
              {getTaskIcon(task.type, task.subType)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-sm font-bold ${
                  isLate ? 'text-red-700' :
                  isToday ? 'text-amber-700' :
                  'text-slate-700'
                }`}>
                  {task.note}
                </span>
                {isLate && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">En retard</span>
                )}
                {isToday && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded">Aujourd'hui</span>
                )}
                {task.type === 'SEQUENCE' && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded">Séquence</span>
                )}
              </div>
              <div className="flex items-center space-x-2 text-xs text-slate-600 mb-1">
                <Users className="w-3 h-3" />
                <span className="font-medium">{task.leadName}</span>
              </div>
              {(task.leadAddress || task.leadCity) && (
                <div className="flex items-center space-x-2 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">
                    {task.leadAddress}{task.leadCity ? `, ${task.leadCity}` : ''}
                  </span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-xs text-slate-400 mt-2">
                <Calendar className="w-3 h-3" />
                <span>{new Date(task.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8 h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          <p className="text-slate-500 mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Dashboard Marketing</h2>
        <p className="text-slate-500 text-sm mt-1">Vue d'ensemble de vos tâches et leads</p>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
          <div className="p-3 bg-red-50 rounded-lg">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-slate-500 font-medium">En retard</p>
            <p className="text-2xl font-bold text-slate-800">{overdueTasks.length}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
          <div className="p-3 bg-amber-50 rounded-lg">
            <Clock className="w-6 h-6 text-amber-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-slate-500 font-medium">Aujourd'hui</p>
            <p className="text-2xl font-bold text-slate-800">{todayTasks.length}</p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex items-center">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm text-slate-500 font-medium">7 prochains jours</p>
            <p className="text-2xl font-bold text-slate-800">{upcomingTasks.length}</p>
          </div>
        </div>
      </div>

      {/* Section Tâches en retard */}
      {overdueTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-bold text-slate-900">Tâches en retard</h3>
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
              {overdueTasks.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overdueTasks.map(task => renderTaskCard(task))}
          </div>
        </div>
      )}

      {/* Section Tâches du jour */}
      {todayTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="w-5 h-5 text-amber-600" />
            <h3 className="text-lg font-bold text-slate-900">Tâches du jour</h3>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
              {todayTasks.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {todayTasks.map(task => renderTaskCard(task))}
          </div>
        </div>
      )}

      {/* Section Tâches à venir */}
      {upcomingTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-slate-900">Tâches à venir (7 jours)</h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
              {upcomingTasks.length}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingTasks.map(task => renderTaskCard(task))}
          </div>
        </div>
      )}

      {/* Message si aucune tâche */}
      {overdueTasks.length === 0 && todayTasks.length === 0 && upcomingTasks.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <p className="text-slate-700 font-medium text-lg mb-2">Aucune tâche en attente</p>
          <p className="text-slate-500 text-sm">Toutes vos tâches sont à jour !</p>
        </div>
      )}
    </div>
  );
};
